import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../services/API/api.service';
import { AlertService } from '../../services/Alert/alert.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {

  orderDetails: any = {};
  isLoading: boolean = false;
  orderId: number = 0;
  orderTips: number = 0;
  orderTax: number = 0;

  constructor(
    private apiService: ApiService,
    private alertService: AlertService,
    private route: ActivatedRoute
  ) {
    this.route.params.subscribe(params => {
      if(params.id){
        this.orderId = params.id;
        this.getOrderDetails();
      }
    });
  }

  ngOnInit(): void {}

  getOrderDetails(){
    this.isLoading = true;
    this.apiService.orderDetails(this.orderId).subscribe({
      next: response => {
        this.orderDetails = response.data.OrderDetails;
        console.log(this.orderDetails);
        this.isLoading = false;
      }, 
      error: (error: HttpErrorResponse) => {
        this.alertService.danger(error.error.message);
        this.isLoading = false;
      }
    });
  }

  // Round to nearest 10 cents (0.10)
  roundToNearestTen(value: number): number {
    return Math.round(value * 10) / 10;
  }

  // Check if there are any products (regular or custom)
  hasAnyProducts(): boolean {
    const hasRegularProducts = this.orderDetails?.products && this.orderDetails.products.length > 0;
    const hasCustomProducts = this.orderDetails?.custom_products && this.orderDetails.custom_products.length > 0;
    return hasRegularProducts || hasCustomProducts;
  }

  // Check if regular products exist
  hasRegularProducts(): boolean {
    return this.orderDetails?.products && this.orderDetails.products.length > 0;
  }

  // Check if custom products exist
  hasCustomProducts(): boolean {
    return this.orderDetails?.custom_products && this.orderDetails.custom_products.length > 0;
  }

  // Calculate extras total for a product (multiplied by product quantity)
  calculateExtrasTotal(extras: any[], productQuantity: number): number {
    if (!extras || extras.length === 0) return 0;
    
    const extrasTotal = extras.reduce(
      (sum: number, extra: any) => sum + (extra.price * (extra.quantity || 1) * productQuantity), 
      0
    );
    
    return this.roundToNearestTen(extrasTotal);
  }

  // Calculate sides total for a product (multiplied by product quantity)
  calculateSidesTotal(sides: any[], productQuantity: number): number {
    if (!sides || sides.length === 0) return 0;
    
    const sidesTotal = sides.reduce(
      (sum: number, side: any) => sum + (side.price * (side.quantity || 1) * productQuantity), 
      0
    );
    
    return this.roundToNearestTen(sidesTotal);
  }

  // Calculate product base total (price * quantity) for regular products
  calculateProductBaseTotal(price: number, quantity: number): number {
    const total = price * quantity;
    return this.roundToNearestTen(total);
  }

  // Calculate complete regular product total including extras and sides
  calculateProductTotal(product: any): number {
    const baseTotal = this.calculateProductBaseTotal(product.price, product.quantity);
    const extrasTotal = this.calculateExtrasTotal(product.extras, product.quantity);
    const sidesTotal = this.calculateSidesTotal(product.sides, product.quantity);
    
    const productTotal = baseTotal + extrasTotal + sidesTotal;
    return this.roundToNearestTen(productTotal);
  }

  // Calculate ingredients total for a custom product
  calculateIngredientsTotal(ingredients: any[], quantity: number): number {
    if (!ingredients || ingredients.length === 0) return 0;
    
    const total = ingredients.reduce(
      (sum: number, ingredient: any) => sum + (ingredient.totalPrice || 0), 
      0
    ) * quantity;
    
    return this.roundToNearestTen(total);
  }

  // Calculate custom product base price (category default + size)
  calculateCustomProductBasePrice(customProduct: any): number {
    const basePrice = customProduct.category?.default_price || 0;
    const sizePrice = customProduct.category?.selected_size?.price || 0;
    return this.roundToNearestTen(basePrice + sizePrice);
  }

  // Calculate custom product total
  calculateCustomProductTotal(customProduct: any): number {
    const baseTotal = this.calculateCustomProductBasePrice(customProduct) * customProduct.quantity;
    const ingredientsTotal = this.calculateIngredientsTotal(customProduct.ingredients, customProduct.quantity);
    
    const total = baseTotal + ingredientsTotal;
    return this.roundToNearestTen(total);
  }

  // Format ingredient display text
  getIngredientDisplay(ingredient: any): string {
    let display = ingredient.name;
    if (ingredient.quantity > 1) {
      display += ` (${ingredient.quantity}x)`;
    }
    if (ingredient.half && ingredient.half !== 'whole') {
      display += ` (${ingredient.half})`;
    }
    return display;
  }

  // Calculate subtotal for regular products
  calculateRegularProductsSubtotal(): number {
    if (!this.hasRegularProducts()) return 0;
    
    const subtotal = this.orderDetails.products.reduce(
      (sum: number, product: any) => sum + this.calculateProductTotal(product), 
      0
    );
    
    return this.roundToNearestTen(subtotal);
  }

  // Calculate subtotal for custom products
  calculateCustomProductsSubtotal(): number {
    if (!this.hasCustomProducts()) return 0;
    
    const subtotal = this.orderDetails.custom_products.reduce(
      (sum: number, customProduct: any) => sum + this.calculateCustomProductTotal(customProduct), 
      0
    );
    
    return this.roundToNearestTen(subtotal);
  }

  // Calculate total subtotal including all products
  calculateSubtotal(): number {
    const regularSubtotal = this.calculateRegularProductsSubtotal();
    const customSubtotal = this.calculateCustomProductsSubtotal();
    
    return this.roundToNearestTen(regularSubtotal + customSubtotal);
  }

  // Get rounded delivery cost
  getRoundedDeliveryCost(): number {
    if (!this.orderDetails?.deliveryCost) return 0;
    return this.roundToNearestTen(this.orderDetails.deliveryCost);
  }

  // Get rounded service cost
  getRoundedServiceCost(): number {
    if (!this.orderDetails?.serviceCost) return 0;
    return this.roundToNearestTen(this.orderDetails.serviceCost);
  }

  // Get rounded tips
  getRoundedTips(): number {
    return this.roundToNearestTen(this.orderTips);
  }

  // Calculate total with all rounded values
  calculateTotal(): number {
    const subtotal = this.calculateSubtotal();
    const delivery = this.getRoundedDeliveryCost();
    const service = this.getRoundedServiceCost();
    const tips = this.getRoundedTips();
    
    const total = subtotal + delivery + service + tips;
    return this.roundToNearestTen(total);
  }

  // Check if product has extras
  hasExtras(product: any): boolean {
    return product.extras && product.extras.length > 0;
  }

  // Check if product has sides
  hasSides(product: any): boolean {
    return product.sides && product.sides.length > 0;
  }

  // Check if custom product has ingredients
  hasIngredients(customProduct: any): boolean {
    return customProduct.ingredients && customProduct.ingredients.length > 0;
  }

  // This will be used later when you implement tips
  updateOrderTotalWithTips(tipsAmount: number): void {
    this.orderTips = this.roundToNearestTen(tipsAmount);
  }

  // Get tax amount (if needed separately)
  getTaxAmount(): number {
    return this.roundToNearestTen(this.orderTax);
  }
}